import { createFeatureSelector, createSelector } from '@ngrx/store';
import { PaymentState, PaymentSubject } from './payment.state';
import { PaymentAppPayOutput, TransactionData, TransactionResultEnum } from '../../../payment/models/paymentOutput';
import { ContractCP } from '../../../models/contract-model';
import { Invoice } from '../../../models/invoice-model';
import { getContractsTotal, getSelectedContracts, getSelectedContractsModels } from '../contract/contract.selectors';
import { getInvoicesTotal, getSelectedInvoices, getSelectedInvoicesModels } from '../invoices/invoices.selectors';

export const selectPaymentState = createFeatureSelector<PaymentState>('payment');

export const selectMultipaymentBoundaries = createSelector(selectPaymentState, (state: PaymentState) => state.paymentBoundaries);

export const selectTransactionLoaded = createSelector(selectPaymentState, (state: PaymentState) => state.transactionLoaded);

export const selectPaymentAppInput = createSelector(selectPaymentState, (state: PaymentState) => state.paymentAppInput);

export const selectConfig = createSelector(selectPaymentState, (state: PaymentState) => {
    state.customerId, state.organizationId;
});

export const selectPaymentAppOutput = createSelector(selectPaymentState, (state: PaymentState) => state.paymentAppOutput);

export const selectErrorString = createSelector(selectPaymentState, (state: PaymentState) => state.error);

export const selectDialogEnabled = createSelector(selectPaymentState, (state: PaymentState) => state.dialogEnabled);

export const selectLastPaymentDetail = createSelector(selectPaymentState, (state: PaymentState) => state.lastPaymentDetail);

export const selectLastPaymentConfirmationPending = createSelector(selectPaymentState, (state: PaymentState) => state.lastPaymentConfirmationPending === true);

export const selectCrossAppEvent = createSelector(selectPaymentState, (state: PaymentState) => state.crossAppEvent);

export const selectPaymentSectionEnabled = createSelector(selectPaymentState, (state: PaymentState) => state.paymentSectionEnabled);

export const selectIsPaymentProcessorAvailable = createSelector(selectPaymentState, (state: PaymentState) => state.isPaymentProcessorAvailable);

export const selectLastPaymentDetailTransactionData = createSelector(selectLastPaymentDetail, (lastPaymentDetail: PaymentAppPayOutput | undefined) => lastPaymentDetail?.TransactionData);

export const selectLastPaymentDetailTransactionId = createSelector(selectLastPaymentDetail, (lastPaymentDetail: PaymentAppPayOutput | undefined) => lastPaymentDetail?.TransactionId);

export const selectLastPaymentDetailTransactionStatus = createSelector(selectLastPaymentDetail, (lastPaymentDetail: PaymentAppPayOutput | undefined) => lastPaymentDetail?.TransactionStatus);

export const selectIsPaymentApproved = createSelector(selectLastPaymentDetailTransactionData, (transactionData: TransactionData | undefined) => {
    if (transactionData && transactionData?.TransactionResultEnum) {
        const resultEnum: TransactionResultEnum = transactionData?.TransactionResultEnum;
        return resultEnum === TransactionResultEnum.APPROVED || resultEnum === TransactionResultEnum.APPROVEDWITHWARNING || resultEnum === TransactionResultEnum.PENDING;
    }
    return false;
});

export const selectTransactionCurrencyCode = createSelector(selectLastPaymentDetailTransactionData, (transactionData: TransactionData | undefined) => {
    return transactionData ? transactionData?.CurrencyCode : '';
});

export const selectDefaultPaymentMid = createSelector(selectPaymentState, (state: PaymentState) => state.defaultPaymentMid);

export const selectPaymentSubject = createSelector(selectPaymentState, (state: PaymentState) => state?.paymentSubject ?? 'contract');

/**
 * ADO-116161: the payment flow resolves its inputs through the payment subject rather than
 * reaching into the contract domain. The invoice arm delegates to the invoices domain
 * (ADO-116163).
 */
export const selectPaymentSelectedIds = createSelector(
    selectPaymentSubject,
    getSelectedContracts,
    getSelectedInvoices,
    (subject: PaymentSubject, selectedContracts: string[], selectedInvoices: string[]): string[] => (subject === 'contract' ? selectedContracts : selectedInvoices)
);

export type PaymentSelection = { subject: 'contract'; models: ContractCP[] } | { subject: 'invoice'; models: Invoice[] };

/**
 * ADO-116161: combined subject + models selector. Returning subject and models as one
 * discriminated-union value (instead of reading selectPaymentSubject and a bare
 * ContractCP[] | Invoice[] union separately) lets the compiler enforce that a given
 * `models` array is only ever narrowed under the `subject` it was actually resolved
 * against — replaces the former isContractModels(subject, models) runtime predicate,
 * which asserted that pairing without checking it.
 */
export const selectPaymentSelection = createSelector(
    selectPaymentSubject,
    getSelectedContractsModels,
    getSelectedInvoicesModels,
    (subject: PaymentSubject, contractModels: ContractCP[], invoiceModels: Invoice[]): PaymentSelection =>
        subject === 'contract' ? { subject, models: contractModels } : { subject, models: invoiceModels }
);

export const selectPaymentSelectedTotal = createSelector(selectPaymentSubject, getContractsTotal, getInvoicesTotal, (subject: PaymentSubject, contractsTotal: number, invoicesTotal: number): number =>
    subject === 'contract' ? contractsTotal : invoicesTotal
);
